import { PropertyInquiryDto } from '@tallo/investor/property';
import { RentStage } from '@tallo/property';

import { byStages, InquiryFilter, InquiryOrder } from './inquiry-filter.const';

export enum ApplicationsFilter {
  ALL = 'All',
  WANTS_TO_APPLY = 'Wants to apply',
  INVITED = 'Invited to apply',
  IN_PROGRESS = 'In progress',
  PENDING = 'Waiting for review',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
}

/**
 * Set by the backend while the ask is unanswered, for 30 days from the latest ask. An ask the owner ignored stays
 * listed but no longer waits on them.
 */
const wantsToApply: InquiryFilter = (inquiry) => !!inquiry.applicationRequestedAt;
const waitsOnOwner: InquiryFilter = (inquiry) => wantsToApply(inquiry) && !inquiry.applicationRequestIgnored;

const askedAt = (inquiry: PropertyInquiryDto) => new Date(inquiry.applicationRequestedAt ?? 0).getTime();

const inApplication = byStages(
  RentStage.APPLICATION_INVITE_SENT,
  RentStage.APPLICATION_ACCEPTED,
  RentStage.APPLICATION_REJECTED,
  RentStage.APPLICATION_COMPLETED,
  RentStage.APPLICATION_IN_PROGRESS,
);

export const applicationsFilterPredicates = new Map<ApplicationsFilter, InquiryFilter>([
  [ApplicationsFilter.ALL, (inquiry) => inApplication(inquiry) || wantsToApply(inquiry)],
  [ApplicationsFilter.WANTS_TO_APPLY, wantsToApply],
  [ApplicationsFilter.INVITED, byStages(RentStage.APPLICATION_INVITE_SENT)],
  [ApplicationsFilter.IN_PROGRESS, byStages(RentStage.APPLICATION_IN_PROGRESS)],
  [ApplicationsFilter.PENDING, byStages(RentStage.APPLICATION_COMPLETED)],
  [ApplicationsFilter.ACCEPTED, byStages(RentStage.APPLICATION_ACCEPTED)],
  [ApplicationsFilter.REJECTED, byStages(RentStage.APPLICATION_REJECTED)],
]);

/** The latest ask comes first */
export const applicationsFilterOrders = new Map<ApplicationsFilter, InquiryOrder>([
  [ApplicationsFilter.WANTS_TO_APPLY, (a, b) => askedAt(b) - askedAt(a)],
]);

/** The two filters that hold work for the owner */
export const applicationsCountedFilters = [ApplicationsFilter.WANTS_TO_APPLY, ApplicationsFilter.PENDING];

/** A count that reads differently from its list: the asks the owner ignored are listed, not counted */
export const applicationsCountPredicates = new Map<ApplicationsFilter, InquiryFilter>([
  [ApplicationsFilter.WANTS_TO_APPLY, waitsOnOwner],
]);
